import { useMemo, useState } from 'react'
import { n } from '../format'
import { newPreferRule, summarizeResolution } from '../resolveSpec'

/**
 * The rules that decide which copy of a duplicate group survives.
 *
 * Built the same way a filter is: pick from the file's own columns, stack the
 * rules in the order they should be applied. The catalogue of rule types comes
 * from the server (/api/meta), so what this offers is exactly what it can run.
 */

/** Values are a priority list, so the editor keeps the user's order. */
function ValuePriority({ rule, column, update }) {
  const [text, setText] = useState(null) // non-null while the textarea is being edited
  const listed = rule.values ?? []
  const value = text ?? listed.join('\n')

  const commit = (next) => {
    setText(next)
    update({ values: next.split('\n').map((s) => s.trim()).filter(Boolean) })
  }
  const append = (v) => {
    if (listed.includes(v)) return
    const next = [...listed, v]
    setText(next.join('\n'))
    update({ values: next })
  }

  return (
    <div className="value-list">
      <textarea
        className="input mono"
        rows={Math.min(6, Math.max(2, listed.length + 1))}
        placeholder={'Best value first,\none per line'}
        value={value}
        onChange={(e) => commit(e.target.value)}
        onBlur={() => setText(null)}
      />
      {/* Low-cardinality columns are sampled, so their real values can be clicked in. */}
      {column?.distinct?.length > 0 && (
        <div className="value-chips">
          {column.distinct.slice(0, 12).map((d) => (
            <button
              key={d.value}
              className={`chip ${listed.includes(d.value) ? 'on' : ''}`}
              title={`${d.value} · ${n(d.count)} rows in the sample`}
              onClick={() => append(d.value)}
            >
              {d.value || '(empty)'}
            </button>
          ))}
        </div>
      )}
      {listed.length > 1 && (
        <p className="footnote">
          {listed[0]} beats {listed[1]}
          {listed.length > 2 ? ` beats ${listed.slice(2).join(' beats ')}` : ''}.
        </p>
      )}
    </div>
  )
}

function PreferCard({ rule, index, last, ctx, onChange, onMove, onRemove }) {
  const { columns, preferences, byName, groups, merge } = ctx
  const pref = preferences.find((p) => p.id === rule.prefer) ?? preferences[0]
  const column = byName.get(rule.column)
  const field = rule.scope === 'field'

  return (
    <div className={`rule resolve-rule${field ? ' field-rule' : ''}`}>
      <div className="rule-head">
        <span className="rule-join">
          {field ? 'for that field,' : index === 0 ? 'start from the' : 'then the'}
        </span>
        <span className="rule-order">{index + 1}</span>
        <span className="rule-moves">
          <button className="icon-btn" title="Move up" disabled={index === 0} onClick={() => onMove(-1)}>
            ↑
          </button>
          <button className="icon-btn" title="Move down" disabled={last} onClick={() => onMove(1)}>
            ↓
          </button>
          <button className="icon-btn" title="Remove rule" onClick={onRemove}>
            ×
          </button>
        </span>
      </div>

      <select
        className="input"
        value={rule.prefer}
        onChange={(e) => onChange({ prefer: e.target.value })}
      >
        {groups.map(([group, prefs]) => (
          <optgroup key={group} label={group}>
            {prefs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {pref?.column && (
        <select className="input" value={rule.column} onChange={(e) => onChange({ column: e.target.value })}>
          {columns.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
              {c.kind === 'number' ? '  #' : ''}
            </option>
          ))}
        </select>
      )}

      {pref?.values && <ValuePriority rule={rule} column={column} update={onChange} />}

      {/* Only meaningful once a record is being assembled: a field rule answers
          for its own column instead of choosing which copy to start from. */}
      {merge && pref?.column && (
        <div className="scope-choice">
          {[
            ['row', 'pick the copy', 'Ranks the copies — its values are used for every field that has one'],
            ['field', `set ${rule.column || 'this field'} only`, 'Decides this one column, whatever the copy we started from says'],
          ].map(([id, label, title]) => (
            <button
              key={id}
              className={`seg ${(rule.scope === 'field' ? 'field' : 'row') === id ? 'on' : ''}`}
              title={title}
              onClick={() => onChange({ scope: id })}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="rule-toggles">
        {pref?.dates && (
          <label className="case-toggle" title="Read 11/02/2026 as 11 February, not 2 November">
            <input
              type="checkbox"
              checked={!!rule.dayFirst}
              onChange={(e) => onChange({ dayFirst: e.target.checked })}
            />
            day first
          </label>
        )}
        {pref?.cased && (
          <label className="case-toggle">
            <input
              type="checkbox"
              checked={!!rule.caseSensitive}
              onChange={(e) => onChange({ caseSensitive: e.target.checked })}
            />
            match case
          </label>
        )}
      </div>

      {pref?.hint && <p className="footnote">{pref.hint}</p>}
    </div>
  )
}

export function ResolutionRules({ columns, preferences, tiebreaks, maxRules, resolution, setResolution }) {
  const byName = useMemo(() => new Map(columns.map((c) => [c.name, c])), [columns])
  const groups = useMemo(() => {
    const out = new Map()
    for (const p of preferences) {
      if (!out.has(p.group)) out.set(p.group, [])
      out.get(p.group).push(p)
    }
    return [...out.entries()]
  }, [preferences])
  const merge = !!resolution.merge
  const ctx = useMemo(
    () => ({ columns, preferences, byName, groups, merge }),
    [columns, preferences, byName, groups, merge],
  )

  const rules = resolution.rules ?? []
  const set = (next) => setResolution({ ...resolution, ...next })
  const fieldRules = rules.filter((r) => r.scope === 'field').length
  const summary = useMemo(
    () => summarizeResolution(resolution, preferences),
    [resolution, preferences],
  )

  const replace = (i, patch) => set({ rules: rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) })
  const remove = (i) => set({ rules: rules.filter((_, j) => j !== i) })
  const move = (i, by) => {
    const to = i + by
    if (to < 0 || to >= rules.length) return
    const next = [...rules]
    ;[next[i], next[to]] = [next[to], next[i]]
    set({ rules: next })
  }

  if (preferences.length === 0) return null

  return (
    <div className="resolve">
      <div className="resolve-head">
        <h3>{merge ? 'How to build the record' : 'Which copy to keep'}</h3>
        {rules.length > 0 && (
          <span className="count">
            {rules.length} rule{rules.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* The rules said out loud, so what the controls below add up to can be
          read without reverse-engineering them one card at a time. */}
      <div className="resolve-summary">
        {summary.map((line, i) => (
          <p key={i} className={i === 0 ? 'summary-lead' : 'summary-line'}>
            {line}
          </p>
        ))}
      </div>

      {/* The choice that changes what the other rules mean, so it comes first. */}
      <label className="merge-toggle">
        <input type="checkbox" checked={merge} onChange={(e) => set({ merge: e.target.checked })} />
        <span className="merge-text">
          <span className="merge-label">Build one clean record per duplicate</span>
          <span className="mode-blurb">
            {merge
              ? 'Each field is taken from the best copy that has a value, so a record can draw its name from one copy and its phone from another.'
              : 'Off: one whole copy is kept as-is and the rest are dropped, blanks included.'}
          </span>
        </span>
      </label>

      <div className="rules">
        {rules.map((rule, i) => (
          <PreferCard
            key={rule.id}
            rule={rule}
            index={i}
            last={i === rules.length - 1}
            ctx={ctx}
            onChange={(patch) => replace(i, patch)}
            onMove={(by) => move(i, by)}
            onRemove={() => remove(i)}
          />
        ))}
      </div>

      <div className="panel-actions tight">
        <button
          className="btn tiny"
          disabled={rules.length >= maxRules}
          title={rules.length >= maxRules ? `At most ${maxRules} rules.` : 'Tried after the rules above'}
          onClick={() => set({ rules: [...rules, newPreferRule(columns, preferences)] })}
        >
          + Add rule
        </button>
      </div>

      <label className="tiebreak">
        <span>still tied? {merge ? 'start from the' : 'keep the'}</span>
        <select
          className="input tiny"
          value={resolution.tiebreak === 'last' ? 'last' : 'first'}
          onChange={(e) => set({ tiebreak: e.target.value })}
        >
          {tiebreaks.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      <p className="footnote">
        Rules are tried in order and the first one that separates two copies decides. A blank cell never
        wins a rule.
      </p>
      {!merge && fieldRules > 0 && (
        <p className="footnote warn-note">
          {fieldRules} rule{fieldRules === 1 ? '' : 's'} set a single field, which only applies while
          building one clean record. Tick the box above for {fieldRules === 1 ? 'it' : 'them'} to take effect.
        </p>
      )}
    </div>
  )
}
